<template>
  <div
    v-if="pendingRequests.length > 0"
    class="text-left text-body-color"
  >
    <h2
      v-if="screenSize !== 'mobile'"
      class="mb-4 flex-1 text-left text-lg text-dark-gray"
    >
      Requests
    </h2>
    <p class="mb-6">
      Keep your account secure by only accepting requests from users with a
      name, email address, and phone number you recognize.
    </p>
    <ul class="mb-12 flex flex-col gap-6">
      <li
        v-for="record in pendingRequests"
        :key="record.id"
      >
        <div
          class="w-full rounded-md border-2 border-light-gray p-1md shadow-primary"
          :class="{
            'flex w-full items-end justify-between': screenSize !== 'mobile',
          }"
        >
          <div>
            <div class="mb-4 sm:mb-0">
              <p class="mb-2 text-1lg text-dark-blue">
                {{ record?.user?.first_name }} {{ record?.user?.last_name }}
              </p>
              <p class="mb-1 text-body-color sm:mb-2">
                {{ record?.user?.email || '-' }}
              </p>
              <p class="mb-1 text-body-color sm:mb-2">
                {{
                  record?.user?.phone_e164
                    ? formatPhoneMethod(record?.user?.phone_e164?.slice(-10))
                    : ''
                }}
              </p>
              <p class="mt-2 text-xs text-body-color">
                Requested at {{ formatRequestedDate(record?.created_at) }}
              </p>
            </div>
          </div>
          <div
            class="flex"
            :class="[
              { 'flex flex-col': screenSize === 'mobile' },
              { 'flex-row-reverse gap-2': screenSize !== 'mobile' },
            ]"
          >
            <button-main
              :size="screenSize === 'mobile' ? 'md' : '2md'"
              :class="screenSize === 'mobile' ? 'mb-3' : ''"
              @click="() => onApproveUserRequest(record.id)"
            >
              Accept
            </button-main>
            <button-main
              :size="screenSize === 'mobile' ? 'md' : '2md'"
              variant="secondary"
              @click="onOpenDeclineModal(record.id)"
            >
              Decline
            </button-main>
          </div>
        </div>
      </li>
    </ul>
  </div>
  <template v-else>
    <h2 class="flex-1 text-left text-xl text-dark-gray">Requests</h2>
    <div class="empty-state-container mt-4 rounded-lg bg-off-white">
      <p class="notice-text">
        You currently do not have anybody requesting access to this account.
      </p>
    </div>
  </template>

  <div
    v-if="completedRequests.length > 0"
    class="text-left"
  >
    <p class="mt-8 text-1lg text-dark-gray">Completed Requests</p>

    <div
      v-if="screenSize === 'mobile'"
      class="mt-4 max-w-lg rounded-md border border-light-gray"
    >
      <div
        v-for="(request, index) in completedRequests"
        :key="request.id"
      >
        <completed-request-card
          :request="request"
          :class="index !== completedRequests.length ? 'border-b' : ''"
          :dark-bg="index % 2 !== 0"
        />
      </div>
    </div>
    <template v-else>
      <completed-request-table :completed-requests="completedRequests" />
    </template>
  </div>

  <user-request-modal
    v-if="declineModalVisible"
    :visible="declineModalVisible"
    type="decline"
    @close="onCloseDeclineModal"
    @submit="rejectUserCompanyRequest"
  />
  <confirm-scopes-modal
    v-if="confirmScopesModalVisible"
    :visible="confirmScopesModalVisible"
    @success="approveUserCompanyRequest"
    @close="confirmScopesModalVisible = false"
  />
</template>

<script>
import moment from 'moment';
import CompletedRequestTable from '@/components/carrier/CompletedRequestTable.vue';
import ButtonMain from '@/components/Button.vue';
import UserRequestModal from '@/components/modals/UserRequestModal.vue';
import CompletedRequestCard from '@/components/carrier/Cards/CompletedRequestCard.vue';
import ConfirmScopesModal from '@/components/modals/ConfirmScopesModal.vue';
import { formatPhone } from '@/common/validators';

export default {
  name: 'User Company Requests Index',
  components: {
    ButtonMain,
    UserRequestModal,
    CompletedRequestCard,
    ConfirmScopesModal,
    CompletedRequestTable,
  },
  props: {
    screenSize: String,
  },
  data() {
    return {
      declineModalVisible: false,
      dec: false,
      selectedRecord: 0,
      confirmScopesModalVisible: false,
    };
  },
  computed: {
    pendingRequests() {
      return this.$store.state.userCompanyRequests.collection.filter(
        (item) => item.status === 'pending'
      );
    },
    completedRequests() {
      return this.$store.state.userCompanyRequests.collection.filter(
        (item) => item.status === 'approved'
      );
    },
    loading() {
      return this.$store.state.loading.isLoading;
    },
  },
  watch: {
    '$store.state.userCompanyRequests.collection': function (val) {
      if (Object.keys(val).length > 0) {
        this.$store.commit('loading/loadingToggleOff', null, {
          root: true,
        });
      }
    },
  },
  async created() {
    this.$store.commit('loading/loadingToggleOn', null, {
      root: true,
    });
    await this.$store.dispatch(
      'userCompanyRequests/getUserCompanyRequests',
      {}
    );
  },
  methods: {
    onApproveUserRequest(id) {
      this.confirmScopesModalVisible = true;
      this.selectedRecord = id;
    },
    formatPhoneMethod(phone) {
      if (phone) {
        return formatPhone(phone);
      } else {
        return null;
      }
    },
    formatRequestedDate(date) {
      const requestedAt = moment(date).format('h:mm A  M/D/YY');
      return requestedAt;
    },
    async approveUserCompanyRequest(scopes = []) {
      const approveRes = await this.$store.dispatch(
        'userCompanyRequests/approveUserCompanyRequest',
        { id: this.selectedRecord, scopes }
      );
      if (approveRes.status === 'approved') {
        this.$toast.open({
          message: 'Request accepted',
          type: 'success',
          duration: 5000,
          dismissible: true,
          position: 'top-right',
        });
        this.selectedRecord = 0;
        this.confirmScopesModalVisible = false;
      }
    },
    async rejectUserCompanyRequest() {
      const rejectRes = await this.$store.dispatch(
        'userCompanyRequests/rejectUserCompanyRequest',
        this.recordId
      );
      console.log('Reject', rejectRes);

      if (rejectRes.status === 'rejected') {
        this.declineModalVisible = false;
        this.$toast.open({
          message: 'Request declined',
          type: 'success',
          duration: 5000,
          dismissible: true,
          position: 'top-right',
        });
        await this.$store.dispatch(
          'userCompanyRequests/getUserCompanyRequests',
          {}
        );
      }
    },
    onOpenDeclineModal(id) {
      this.recordId = id;
      this.declineModalVisible = true;
    },
    onCloseDeclineModal() {
      this.declineModalVisible = false;
    },
  },
};
</script>
<style scoped>
.notice-text {
  @apply text-center text-gray-over-dark;
  font-size: 16px;
}

.empty-state-container {
  padding: 50px 16px !important;
}
</style>
